import { InlineFormattedNumber } from '@src/components/InlineFormattedNumber';
import { OptimizedImage } from '@src/components/OptimizedImage';
import { PortfolioHUD } from '@src/components/PortfolioHUD';
import { useMarketDataContext } from '@src/Providers/MarketDataContext';
import {
  CurrencyIcon,
  primeSwapForm,
  useCurrenciesContext,
  usePortal,
  useUserContext,
  useWLOrderFormDispatch,
  useWLOrderFormSelector,
} from '@talos/kyoko';
import clsx from 'clsx';
import { useMemo } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { AnimatedStackOutlet } from '../../components/AnimatedStackOutlet';
import { useStackableRouteId } from '../../hooks/useStackableRouteId';

export function WatchlistLayout() {
  const portalId = useStackableRouteId();
  const { setPortalRef } = usePortal(portalId);
  const match = useMatch('/watchlist');

  return (
    <>
      <AnimatedStackOutlet />
      <div className={clsx('flex w-full grow', match === null && 'pointer-events-none')}>
        {/* Base background route: always rendered */}
        <WatchlistPage />
      </div>
      <div id={`${portalId}-portal`} ref={setPortalRef} />
    </>
  );
}

export const WatchlistPage = () => {
  // TODO(robertz): I have an idea that this should not be all currencies in the system, instead it should be
  // a list of all tradable currencies in the system.
  // And the market data that is being shown is always going to be in [CURRENCY]-USDT
  // which means maybe we should define all tradableCurrencies such that it is a list of all currencies that can be traded with USDT.
  const { currenciesList } = useCurrenciesContext();
  const { marketDataByCurrency, sparklineBySymbol } = useMarketDataContext();
  const dispatch = useWLOrderFormDispatch();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const favoredPairCurrency = useWLOrderFormSelector(state => state.swap.referenceData.favoredPairCurrency);
  const homeCurrency = useWLOrderFormSelector(state => state.swap.referenceData.homeCurrency);

  const filteredCurrencies = useMemo(() => {
    return currenciesList
      .filter(currency => currency.Symbol !== homeCurrency?.Symbol)
      .toSorted((a, b) => a.Symbol.localeCompare(b.Symbol));
  }, [currenciesList, homeCurrency]);

  return (
    <>
      <div className="absolute h-[160px] w-full">
        <div className="relative size-full">
          <OptimizedImage
            name="banner"
            widths={[375, 750, 1125]}
            alt="Mobile and coin floating in space"
            className="absolute size-full object-cover"
            loading="eager"
          />

          <div className="bg-slate-fade-bottom absolute inset-0" />
        </div>
      </div>
      <div className="relative w-full p-3 pt-8" data-testid="watchlist-page">
        <div className="flex w-full flex-col gap-4">
          <h1 className="text-color-text-inverse mx-2 text-xl font-semibold">Hi, {user.DisplayName ?? user.Name}!</h1>
          <PortfolioHUD />

          <div className="border-color-border bg-color-bg-main flex flex-col rounded-md border">
            <div className="flex w-full justify-between px-4 py-2">
              <h3 className="text-color-text text-lg font-bold">Market</h3>
            </div>
            <div className="flex flex-col tracking-wide">
              {filteredCurrencies.map(currency => {
                const { marketData, security } = marketDataByCurrency.get(currency.Symbol) ?? {};

                const sparkline = sparklineBySymbol?.get(`${currency.Symbol}-${favoredPairCurrency?.Symbol}`);
                const percentChange = sparkline?.PercentChange?.toFixed(2);
                const isPositive = percentChange && Number(percentChange) >= 0;

                return (
                  <div
                    key={currency.Symbol}
                    data-testid={`watchlist-item-${currency.Symbol}`}
                    className="border-color-border flex flex-row items-center gap-2 border-t px-6 py-4"
                    onClick={() => {
                      dispatch(primeSwapForm({ toCurrency: currency.Symbol }));
                      navigate('swap');
                    }}
                  >
                    <div className="size-10">
                      <CurrencyIcon currency={currency.Symbol} size={40} />
                    </div>
                    <div className="flex w-full flex-col">
                      <div className="text-color-text flex w-full flex-row justify-between">
                        <h1 className="text-color-text font-bold">{currency.Symbol}</h1>
                        <h1 className="font-bold">
                          <InlineFormattedNumber
                            number={marketData?.Offers.at(0)?.Price ?? marketData?.Bids.at(0)?.Price}
                            currency={security?.QuoteCurrency}
                            showCurrency
                          />
                        </h1>
                      </div>
                      <div className="text-color-text-weaker flex w-full flex-row justify-between text-sm font-semibold">
                        <h2 className="text-color-text-weaker">{currency.Description}</h2>
                        {percentChange && (
                          <div
                            className={clsx(
                              isPositive ? 'text-color-text-success-strong' : 'text-color-text-error-strong',
                              'flex items-center gap-1'
                            )}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="8"
                              height="8"
                              viewBox="0 0 8 8"
                              fill="none"
                              className={clsx(!isPositive && 'rotate-180')}
                            >
                              <path
                                d="M4.66635 1.3352L7.7221 5.04628C8.31575 5.76725 7.8953 7 7.05575 7L0.944251 7C0.104695 7 -0.315754 5.76725 0.277902 5.04628L3.33365 1.33521C3.70166 0.888265 4.29834 0.888265 4.66635 1.3352Z"
                                fill="currentColor"
                              />
                            </svg>
                            % {percentChange}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-help-fade-left border-color-border-weak flex w-full flex-row items-center gap-4 rounded-lg border p-3">
            <div className="flex aspect-square max-w-[125px] flex-[3] items-center justify-center p-2">
              <OptimizedImage name="help" widths={[125, 250]} alt="question-mark" />
            </div>
            <div className="flex flex-[7] flex-col">
              <h2 className="text-lg font-bold">We&apos;re here to help!</h2>
              <p className="text-color-text-weak">Got questions?</p>
              <div>
                <Link to="https://help.gcash.com/hc/en-us/sections/51913945263897" className="text-color-text-info">
                  Learn more
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
